import type { BrandDNA } from '@/entities/brand';
import { ResolvedPromptSchema, type ResolvedPrompt, type Template } from '@/entities/template';
import type { ReplicateClient } from '@/shared/api/replicate';
import { parseJsonLoose, runLlm } from '@/shared/api/llm';

const SYSTEM = `You fill in [BRACKETED_PLACEHOLDERS] in ad-prompt templates with brand-specific values.
You must return ONLY valid JSON: {"prompt":"...full filled prompt..."}.
Do not change the structure of the template — only replace placeholders, including hex codes.
If the brand supplies a non-empty promptModifier, begin the resolved prompt with it verbatim. If promptModifier is missing or empty, emit nothing in its place — drop the [IMAGE_GENERATION_PROMPT_MODIFIER] line entirely (no leftover placeholder, no stray quotes), and let the resolved prompt start with the next non-empty line.
Replace placeholders with specific values — real numbers, concrete outcomes, named sensory or behavioral detail. Never leave vague or example-style filler ("results fast", "great product").
Headline-style placeholders (HEADLINE, HOOK, PAIN_HOOK, MANIFESTO_BELIEF, NOTE_OPENER) must be ≤7 words and written in the brand's headlineTone register.

Branch resolution: when you encounter a placeholder of the form [IF_APP: A | IF_PHYSICAL: B | IF_SERVICE: C], emit ONLY the branch matching the brand's productType. Drop the other branches and the surrounding brackets entirely — the resolved prompt must contain no "IF_APP", "IF_PHYSICAL", "IF_SERVICE", or vertical-bar separators from the branch syntax. Branches may themselves contain other placeholders; fill those normally inside the chosen branch.

Reference-image fidelity: when the template contains the phrase "Preserve the supplied reference image exactly", keep that phrase verbatim in the output — it is the model instruction that locks the edit-capable image model onto the user's upload.

Reference image (for the prompt-writer): if a product reference image is attached to this turn, ground product description, color, finish, label text, and visible packaging details in what you actually see — overriding the brand DNA's text fields when they conflict with the photo. The image refines content, not structure: never let it override branch resolution, palette rules, or the template's focal hierarchy.

Concept brief: if the user supplies a "Concept brief" section, weave its scene, subject, and mood into placeholder fills wherever the template allows (lifestyle scenes, hook copy, headlines, props, sensory details). The brief refines content, not structure — never let it override the template's focal hierarchy, branch resolution, color palette, or reference-image fidelity rules.`;

import { z } from 'zod';
const FillSchema = z.object({ prompt: z.string().min(20) });

export interface FillInput {
  template: Template;
  dna: BrandDNA;
  brandName: string;
  llmModelId: string;
  /** Optional per-batch user direction. When set, the LLM weaves it into placeholder fills. */
  conceptBrief?: string;
  /** Optional product reference images. Only the first is forwarded to the LLM. */
  imageUrls?: readonly string[];
  signal?: AbortSignal;
}

export async function fillTemplate(
  client: ReplicateClient,
  input: FillInput,
): Promise<{ resolved: ResolvedPrompt; replicateId: string; inputTokens?: number; outputTokens?: number; predictTimeSec?: number }> {
  const briefSection = input.conceptBrief && input.conceptBrief.trim().length > 0
    ? `\n\nConcept brief (the user's direction for THIS batch — honor it inside the template's structural rules):\n"""\n${input.conceptBrief.trim()}\n"""`
    : '';

  const userPrompt = `Brand: ${input.brandName}
Brand DNA JSON:
${JSON.stringify(input.dna, null, 2)}

Template name: ${input.template.name}
Aspect ratio: ${input.template.aspectRatio}
Template prompt (with [PLACEHOLDERS]):
"""
${input.template.promptTemplate}
"""${briefSection}

Replace the [IMAGE_GENERATION_PROMPT_MODIFIER] placeholder with the brand's promptModifier verbatim (or drop the line entirely if the modifier is empty/absent), then fill all remaining bracketed placeholders with brand-specific values from the DNA. Return JSON: {"prompt": "<full filled prompt>"}.`;

  const llm = await runLlm(client, {
    modelId: input.llmModelId,
    systemPrompt: SYSTEM,
    prompt: userPrompt,
    maxTokens: 2048,
    temperature: 0.3,
    ...(input.imageUrls && input.imageUrls.length > 0 && { imageUrls: input.imageUrls }),
    ...(input.signal && { signal: input.signal }),
  });

  const filled = parseJsonLoose(llm.text, FillSchema);
  const resolved = ResolvedPromptSchema.parse({
    templateId: input.template.id,
    templateName: input.template.name,
    aspectRatio: input.template.aspectRatio,
    needsProductImages: input.template.needsProductImages,
    // Placeholder to satisfy schema; the runner overrides this with the real value.
    numImages: 1,
    prompt: filled.prompt,
  });

  return {
    resolved,
    replicateId: llm.replicateId,
    ...(llm.inputTokens !== undefined && { inputTokens: llm.inputTokens }),
    ...(llm.outputTokens !== undefined && { outputTokens: llm.outputTokens }),
    ...(llm.predictTimeSec !== undefined && { predictTimeSec: llm.predictTimeSec }),
  };
}
